/**
 * The flavour wheel's maths (screen 03), drawing the flavour database's
 * wheel: 140 notes in ten families, up to three ingredients over each other.
 *
 * Everything here is pure geometry and selection: where each family's arc
 * runs, where each note sits, how far out a score plots, which notes earn a
 * bold label, how bold labels are pushed apart, and the radar line or bars
 * for each ingredient. The component (web `FlavourWheel.tsx`) places the
 * elements and handles taps; it computes nothing, so the figure can be
 * tested without a DOM, and the screen and the development chat draw exactly
 * the same wheel.
 *
 * ## Coordinates
 *
 * The figure uses the flavour database's own frame so its numbers can be checked
 * against their SVG: a 678.08-unit square viewBox centred on 339.04. Angles
 * are radians, 0 at twelve o'clock, running clockwise.
 *
 * ## Arcs and slots
 *
 * Each family's arc is its share of the notes being shown (all 140, or just
 * the key notes), starting at twelve o'clock in `FLAVOUR_FAMILIES` order, and
 * pulled in by 0.01 rad at each end so neighbouring families do not touch.
 * Each note gets an equal slot within its family; its point and label sit in
 * the slot's middle. With all 140 shown a 14-note family spans 36°, its notes
 * 2.49° apart, and the step across a boundary is 3.64° — what the reference wheel
 * draws. Woody (13 notes) and Earthy (15) are the uneven ones.
 *
 * ## Radius
 *
 * A score is clamped to 0–10 and plotted outward from the family ring:
 * r = 118.41 + 132.48 · v/10, so 10 reaches 250.9, the outermost guide ring.
 * Lime's Sugary (2.2) lands at 147.5556, as the reference wheel draws it.
 *
 * ## Two modes
 *
 * All notes draws every note, one unfilled radar line per ingredient, and
 * picks out the "active" notes in bold in their owner's colour (see
 * `activeNotes`). Key notes drops every note no ingredient rates (see
 * `keyNotes`), lays the families out over what is left, and draws grouped
 * radial bars instead of lines.
 */
import { clampScore, FLAVOUR_FAMILIES, FLAVOUR_NEUTRAL_COLOUR, FLAVOUR_NOTES, FLAVOUR_NOTE_COUNT, } from "./flavour.js";
// ------------------------------------------------------------------ constants
/** The viewBox is `0 0 678.08 678.08`; everything is measured from its centre. */
export const WHEEL_SIZE = 678.08;
export const WHEEL_CENTRE = 339.04;
/** The radii the reference wheel draws at. Changing one changes the whole figure. */
export const WHEEL_RADII = {
    /** The centre disc carrying the count of notes shown. */
    hub: 46.2,
    /** The coloured family ring. Its inner edge is ours; the doc gives only the outer. */
    familyInner: 104,
    familyOuter: 115.4,
    /** Where a score of 0 plots. */
    base: 118.41,
    /** How much further a score of 10 reaches. */
    span: 132.48,
    /** Guide rings at 25, 50, 75 and 100%. */
    rings: [151.5, 184.6, 217.8, 250.9],
    /**
     * Where note labels start. The doc does not give it; just outside the 100%
     * ring, so a full-scale point never runs into its own label.
     */
    label: 262,
};
/** Each family's arc is pulled in by this much at both ends, in radians. */
export const FAMILY_INSET = 0.01;
/**
 * The selected ingredients' colours, in selection order. The first two are
 * the reference wheel's; the doc never checked a third, so the third is our pick
 * (the Spice orange), chosen to sit apart from the other two.
 */
export const INGREDIENT_COLOURS = ["#59A0C4", "#BD768F", "#DF8353"];
/** The reference wheel compares at most three. */
export const MAX_WHEEL_INGREDIENTS = 3;
/** Label styles for All notes mode, as the reference wheel sets them. */
export const LABEL_STYLE = {
    active: { size: 13.6, weight: 600, opacity: 1 },
    other: { size: 8.8, weight: 400, opacity: 0.82, colour: FLAVOUR_NEUTRAL_COLOUR },
    /** Key notes mode: one size for every label. The doc does not give it; ours. */
    key: { size: 10.5, weight: 500, opacity: 1 },
};
/** The colour for the ingredient at `position` in the selection. */
export function ingredientColour(position) {
    return INGREDIENT_COLOURS[position] ?? INGREDIENT_COLOURS[INGREDIENT_COLOURS.length - 1];
}
// -------------------------------------------------------------------- polar
/** A point `r` from the centre at `angle` radians clockwise from twelve, in viewBox units. */
export function wheelPoint(r, angle) {
    return [WHEEL_CENTRE + r * Math.sin(angle), WHEEL_CENTRE - r * Math.cos(angle)];
}
/** How far out a score plots: 118.41 for 0, 250.89 for 10, clamped. */
export function noteRadius(value) {
    return WHEEL_RADII.base + (WHEEL_RADII.span * clampScore(value)) / 10;
}
const fixed = (v) => Number(v.toFixed(2));
/** The path for a ring segment between radii `r0` < `r1` and angles `a0` < `a1`. */
export function sectorPath(r0, r1, a0, a1) {
    const [x0, y0] = wheelPoint(r1, a0);
    const [x1, y1] = wheelPoint(r1, a1);
    const [x2, y2] = wheelPoint(r0, a1);
    const [x3, y3] = wheelPoint(r0, a0);
    const large = a1 - a0 > Math.PI ? 1 : 0;
    return (`M${fixed(x0)},${fixed(y0)} A${r1},${r1} 0 ${large} 1 ${fixed(x1)},${fixed(y1)}` +
        ` L${fixed(x2)},${fixed(y2)} A${r0},${r0} 0 ${large} 0 ${fixed(x3)},${fixed(y3)} Z`);
}
/**
 * The family arcs and note slots for the notes being shown. `shown` is any
 * set of note indexes (order does not matter); a family with none shown gets
 * no arc at all, so Key notes mode never draws an empty sliver.
 */
export function familyArcs(shown = FLAVOUR_NOTES.map((n) => n.index)) {
    const set = new Set(shown);
    const total = set.size;
    if (total === 0)
        return [];
    const arcs = [];
    let running = 0;
    let offset = 0;
    for (const family of FLAVOUR_FAMILIES) {
        const indexes = family.notes.map((_, k) => offset + k).filter((i) => set.has(i));
        offset += family.notes.length;
        if (indexes.length === 0)
            continue;
        const span = (indexes.length / total) * 2 * Math.PI;
        const start = running + FAMILY_INSET;
        const end = running + span - FAMILY_INSET;
        const u = (end - start) / indexes.length;
        arcs.push({
            family: family.key,
            colour: family.colour,
            start,
            end,
            slots: indexes.map((index, k) => ({
                index,
                family: family.key,
                start: start + k * u,
                end: start + (k + 1) * u,
                angle: start + (k + 0.5) * u,
            })),
        });
        running += span;
    }
    return arcs;
}
/** Every slot in `arcs`, keyed by note index. */
export function slotsByNote(arcs) {
    return new Map(arcs.flatMap((a) => a.slots).map((s) => [s.index, s]));
}
/** The All notes threshold and cap: over 2 of 10, top 20 per ingredient. */
export const ACTIVE_MIN = 2;
export const ACTIVE_TOP = 20;
/** The Key notes threshold and cap: over 1 of 10, top 30 per ingredient. */
export const KEY_MIN = 1;
export const KEY_TOP = 30;
function ownerOf(index, profiles, among) {
    let owner = -1;
    let best = -Infinity;
    for (const i of among ?? profiles.map((_, k) => k)) {
        const v = clampScore(profiles[i].notes[index]);
        if (v > best) {
            best = v;
            owner = i;
        }
    }
    return owner;
}
/** One ingredient's notes over `min` (exclusive), strongest first, the first `top` after `tie`. */
function strongest(profile, min, top, tie) {
    return FLAVOUR_NOTES.map((n) => n.index)
        .filter((i) => clampScore(profile.notes[i]) > min)
        .sort((a, b) => clampScore(profile.notes[b]) - clampScore(profile.notes[a]) || tie(a, b))
        .slice(0, top);
}
const byName = (a, b) => FLAVOUR_NOTES[a].name.localeCompare(FLAVOUR_NOTES[b].name);
const byIndex = (a, b) => a - b;
/** One ingredient's active notes: over 2 of 10, strongest first, ties alphabetical by internal name, top 20. */
export function activeNotesOf(profile) {
    return strongest(profile, ACTIVE_MIN, ACTIVE_TOP, byName);
}
/**
 * The notes All notes mode prints in bold, in vector order: the union of
 * each ingredient's `activeNotesOf`, each owned by whichever ingredient
 * scores it higher (the first on a tie). The centre counts these — 29 for
 * Lime + Ron Bacardi Carta Blanca, as the reference wheel shows.
 */
export function activeNotes(profiles) {
    const union = new Set(profiles.flatMap(activeNotesOf));
    return [...union].sort(byIndex).map((index) => ({ index, owner: ownerOf(index, profiles) }));
}
/**
 * The notes Key notes mode keeps, in vector order: per ingredient, notes over
 * 1 of 10, strongest first, top 30; then the union, each remembering which
 * ingredients qualified it and owned by the highest scorer among those.
 *
 * Ties are broken in vector order here, not alphabetically as All notes does.
 * The doc does not say which; vector order reproduces the reference wheel's 47 notes
 * for Lime + Ron Bacardi Carta Blanca, and alphabetical gives 50.
 */
export function keyNotes(profiles) {
    const lists = profiles.map((p) => new Set(strongest(p, KEY_MIN, KEY_TOP, byIndex)));
    const union = new Set(lists.flatMap((s) => [...s]));
    return [...union].sort(byIndex).map((index) => {
        const qualifyingIngredients = lists.flatMap((s, k) => (s.has(index) ? [k] : []));
        return { index, owner: ownerOf(index, profiles, qualifyingIngredients), qualifyingIngredients };
    });
}
/**
 * Notes active for every ingredient at once — what the development chat is
 * told they share. Fewer than two ingredients share nothing.
 */
export function sharedActiveNotes(profiles) {
    if (profiles.length < 2)
        return [];
    const sets = profiles.map((p) => new Set(activeNotesOf(p)));
    return [...sets[0]].filter((i) => sets.every((s) => s.has(i))).sort(byIndex);
}
// ------------------------------------------------------------------- labels
/** The smallest angle two bold labels may sit apart at radius `r`: 0.95 × 13.6 ÷ r. */
export function minLabelGap(r = WHEEL_RADII.label, size = LABEL_STYLE.active.size) {
    return (0.95 * size) / r;
}
/**
 * Bold labels spread so they do not overlap, as the reference wheel does it: walk the
 * angles in order keeping a stack of clusters; when a label (or a cluster it
 * has grown into) would start closer than `gap` to the end of the cluster
 * before, the two merge. A cluster's labels are spaced evenly at `gap`,
 * centred on the average of their own slot angles. Returns the placed angle
 * for each input angle, in input order.
 *
 * Like the reference wheel's, this does not wrap past twelve o'clock: a crowd either
 * side of the top is spread as two groups.
 */
export function spreadLabels(angles, gap = minLabelGap()) {
    const order = angles.map((a, i) => [a, i]).sort((x, y) => x[0] - y[0]);
    const first = (c) => c.mean - ((c.members.length - 1) * gap) / 2;
    const last = (c) => c.mean + ((c.members.length - 1) * gap) / 2;
    const stack = [];
    for (const [angle, input] of order) {
        stack.push({ members: [{ angle, input }], mean: angle });
        while (stack.length > 1) {
            const top = stack[stack.length - 1];
            const prev = stack[stack.length - 2];
            if (first(top) - last(prev) >= gap - 1e-12)
                break;
            const members = [...prev.members, ...top.members];
            stack.splice(-2, 2, { members, mean: members.reduce((t, m) => t + m.angle, 0) / members.length });
        }
    }
    const out = new Array(angles.length);
    for (const c of stack)
        c.members.forEach((m, k) => (out[m.input] = first(c) + k * gap));
    return out;
}
export function labelPlacement(angle, r = WHEEL_RADII.label) {
    const [x, y] = wheelPoint(r, angle);
    const deg = (angle * 180) / Math.PI;
    const norm = ((angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    const right = norm < Math.PI;
    return { x: fixed(x), y: fixed(y), rotate: fixed(right ? deg - 90 : deg + 90), anchor: right ? "start" : "end" };
}
/**
 * The closed radar line through all 140 of a profile's notes, at the slots
 * `arcs` gives them. `M` to the first, `L` through the rest, `Z` to close.
 */
export function radarPath(profile, arcs) {
    const points = arcs.flatMap((a) => a.slots).map((s) => wheelPoint(noteRadius(profile.notes[s.index] ?? 0), s.angle));
    if (points.length === 0)
        return "";
    return points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${fixed(x)},${fixed(y)}`).join(" ") + " Z";
}
/** The share of a slot that Key notes' bars fill, side by side; the rest is gap. */
const BAR_FILL = 0.78;
/**
 * Everything the component draws, for up to three ingredients (more are
 * ignored) in either mode. With none, the families and grey labels still draw
 * and the count is 0.
 */
export function wheelFigure(items, mode) {
    const shown = items.slice(0, MAX_WHEEL_INGREDIENTS);
    const profiles = shown.map((i) => i.profile);
    if (mode === "key") {
        const key = keyNotes(profiles);
        const arcs = familyArcs(key.map((k) => k.index));
        const slots = slotsByNote(arcs);
        const style = LABEL_STYLE.key;
        const notes = key.map((k) => {
            const slot = slots.get(k.index);
            return {
                ...slot,
                label: FLAVOUR_NOTES[k.index].label,
                active: true,
                owner: k.owner,
                colour: shown[k.owner]?.colour ?? FLAVOUR_NEUTRAL_COLOUR,
                size: style.size,
                weight: style.weight,
                opacity: style.opacity,
                labelAngle: slot.angle,
                placement: labelPlacement(slot.angle),
            };
        });
        // The doc counts 62 bars for Lime + Ron Bacardi Carta Blanca; one bar per
        // qualifying ingredient per note gives 60 for that pair. The doc does not
        // say where the other two come from, so this follows the rule it states.
        const bars = key.flatMap((k) => {
            const slot = slots.get(k.index);
            const width = ((slot.end - slot.start) * BAR_FILL) / k.qualifyingIngredients.length;
            const left = slot.angle - (width * k.qualifyingIngredients.length) / 2;
            return k.qualifyingIngredients.map((ing, j) => {
                const value = clampScore(profiles[ing].notes[k.index]);
                return {
                    index: k.index,
                    ingredient: ing,
                    colour: shown[ing].colour,
                    value,
                    d: sectorPath(WHEEL_RADII.base, noteRadius(value), left + j * width, left + (j + 1) * width),
                };
            });
        });
        return { mode, arcs, notes, radar: [], bars, count: key.length };
    }
    const arcs = familyArcs();
    const active = new Map(activeNotes(profiles).map((n) => [n.index, n.owner]));
    const slots = arcs.flatMap((a) => a.slots);
    const activeSlots = slots.filter((s) => active.has(s.index));
    const spread = spreadLabels(activeSlots.map((s) => s.angle));
    const placed = new Map(activeSlots.map((s, k) => [s.index, spread[k]]));
    const notes = slots.map((slot) => {
        const owner = active.get(slot.index);
        const on = owner !== undefined;
        const style = on ? LABEL_STYLE.active : LABEL_STYLE.other;
        const labelAngle = placed.get(slot.index) ?? slot.angle;
        return {
            ...slot,
            label: FLAVOUR_NOTES[slot.index].label,
            active: on,
            owner: on ? owner : -1,
            colour: on ? (shown[owner]?.colour ?? FLAVOUR_NEUTRAL_COLOUR) : LABEL_STYLE.other.colour,
            size: style.size,
            weight: style.weight,
            opacity: style.opacity,
            labelAngle,
            placement: labelPlacement(labelAngle),
        };
    });
    const radar = shown.map((item, ingredient) => ({ ingredient, colour: item.colour, d: radarPath(item.profile, arcs) }));
    return { mode, arcs, notes, radar, bars: [], count: active.size };
}
// ------------------------------------------------------------------- panel
/** How many strongest notes the side panel lists per ingredient: 5 for one, 3 for two, 2 for three. */
export function strongestCount(selected) {
    return selected <= 1 ? 5 : selected === 2 ? 3 : 2;
}
/**
 * Our words for a score, for the note card's closing line. The reference wheel
 * generates a line like "Strong in lime, subtle in ron bacardi carta blanca"
 * from the values, but the doc does not give its thresholds; these are ours.
 */
export function noteStrength(value) {
    const v = clampScore(value);
    if (v >= 5)
        return "strong";
    if (v >= 2.5)
        return "moderate";
    if (v > 0)
        return "subtle";
    return "absent";
}
function listed(names) {
    return names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}
/**
 * The note card's closing line: each strength in turn, strongest first, the
 * ingredients named in lower case as the reference wheel writes them. "Strong in
 * lime, subtle in ron bacardi carta blanca."
 */
export function noteVerdict(index, items) {
    const groups = { strong: [], moderate: [], subtle: [], absent: [] };
    for (const item of items)
        groups[noteStrength(item.profile.notes[index] ?? 0)].push(item.name.toLowerCase());
    const parts = [
        groups.strong.length ? `strong in ${listed(groups.strong)}` : "",
        groups.moderate.length ? `moderate in ${listed(groups.moderate)}` : "",
        groups.subtle.length ? `subtle in ${listed(groups.subtle)}` : "",
        groups.absent.length ? `absent from ${listed(groups.absent)}` : "",
    ].filter(Boolean);
    const line = parts.join(", ");
    return line ? line[0].toUpperCase() + line.slice(1) : "";
}
/** True when `index` is a real note, for guarding taps and props. */
export function isNoteIndex(index) {
    return typeof index === "number" && Number.isInteger(index) && index >= 0 && index < FLAVOUR_NOTE_COUNT;
}
// -------------------------------------------------------------------- chat
/**
 * The opening line for the development chat when a selection is sent to it.
 *
 * Written as a bartender would ask it, with the notes the ingredients share
 * stated (each one's own bold notes, in common), because that is the overlap
 * the wheel shows and the chat cannot see.
 */
export function developmentPrompt(names, shared) {
    if (names.length === 0)
        return "";
    const opening = `Develop a drink built on ${listed(names)}.`;
    if (names.length < 2)
        return opening;
    if (shared.length === 0) {
        return `${opening} They share none of their strongest notes, so tell me what would bridge them.`;
    }
    const noun = shared.length === 1 ? "note" : "notes";
    return `${opening} They share ${shared.length} strong ${noun}: ${shared.join(", ")}.`;
}
//# sourceMappingURL=wheel.js.map